import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CalendarClock, Repeat, Loader2, RefreshCw, CheckCircle2, Bell, CalendarPlus, Zap, Send } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { scheduleTracker, apiWorkorders, automations } from '../../lib/api';
import { toast } from 'sonner';

const WINDOWS = [{ v: 7, l: 'Next 7 days' }, { v: 14, l: 'Next 14 days' }, { v: 30, l: 'Next 30 days' }];

function StatCard({ icon: Icon, label, value, tone, testid, target }) {
  const tones = {
    red: 'border-red-200 bg-red-50 text-red-600',
    amber: 'border-amber-200 bg-amber-50 text-amber-600',
    green: 'border-[#8BC53F]/30 bg-[#8BC53F]/10 text-[#5e8a1f]',
  };
  // These were plain cards. The office tapped "171 Overdue / Missed" expecting
  // to see the 171 and nothing happened - the list was already further down
  // the page, just not reachable from the number.
  const jump = () => {
    const el = document.querySelector(`[data-testid="${target}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <Card role="button" tabIndex={0} onClick={jump}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); jump(); } }}
      className={`p-5 border min-w-0 cursor-pointer transition-shadow hover:shadow-md ${tones[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-white/70 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
        <div className="min-w-0">
          <div className="text-3xl font-extrabold leading-none text-slate-900">{value}</div>
          <div className="text-xs font-semibold uppercase tracking-wide mt-1 leading-tight">{label}</div>
        </div>
      </div>
    </Card>
  );
}

export default function ServiceTracker() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [withinDays, setWithinDays] = useState(7);
  const [busy, setBusy] = useState('');

  const load = useCallback(async (days) => {
    setLoading(true);
    try {
      const d = await scheduleTracker.get(days);
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the service tracker');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(withinDays); }, [load, withinDays]);

  const markComplete = async (wo) => {
    setBusy(wo.id);
    try {
      await apiWorkorders.update(wo.id, { ...wo, status: 'Completed' });
      toast.success(`${wo.customer} marked complete`);
      await load(withinDays);
    } catch { toast.error('Could not update job'); } finally { setBusy(''); }
  };

  const remind = async (wo) => {
    setBusy(wo.id);
    try {
      const r = await automations.notifyWorkorder(wo.id, 'reminder');
      toast.success(`Reminder sent via ${r.channels.join(', ')}`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not send reminder'); }
    finally { setBusy(''); }
  };

  const book = async (cand) => {
    setBusy(cand.customerId);
    try {
      const r = await scheduleTracker.autoBook({ customerId: cand.customerId, date: cand.suggestedDate, service: cand.service, tech: cand.tech });
      toast.success(`Next visit booked for ${cand.customer} on ${r.workorder.date}`);
      await load(withinDays);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not book visit'); }
    finally { setBusy(''); }
  };

  const bookAll = async () => {
    if (!data?.rebook?.length) return;
    setBusy('all');
    try {
      const r = await scheduleTracker.autoBookAll(withinDays);
      toast.success(`Auto-booked ${r.booked} recurring visit${r.booked === 1 ? '' : 's'}`);
      await load(withinDays);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not auto-book'); }
    finally { setBusy(''); }
  };

  const remindToday = async () => {
    setBusy('today');
    try {
      const r = await automations.runToday();
      if (r.total === 0) toast.info('No jobs scheduled for today.');
      else toast.success(`Today's reminders: ${r.sent} sent, ${r.skipped} skipped (${r.total} job${r.total === 1 ? '' : 's'})`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Reminders are turned off — enable them in Settings → Automations."); }
    finally { setBusy(''); }
  };

  const s = data?.summary || { overdue: 0, dueSoon: 0, rebook: 0 };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="service-tracker-page">
      <div className="flex flex-wrap justify-between gap-3 items-start">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Service Tracker</h1>
          <p className="text-slate-500 mt-1">Catch missed visits, stay ahead of due jobs, and auto-book recurring service.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Button onClick={remindToday} disabled={busy === 'today'} className="gap-1.5 bg-[#0d2818] hover:bg-[#16351f]" data-testid="remind-today-btn">
            {busy === 'today' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}Send today's reminders
          </Button>
          <Select value={String(withinDays)} onValueChange={(v) => setWithinDays(Number(v))}>
            <SelectTrigger className="h-10 w-[150px]" data-testid="tracker-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>{WINDOWS.map(w => <SelectItem key={w.v} value={String(w.v)}>{w.l}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" onClick={() => load(withinDays)} className="gap-1.5" data-testid="tracker-refresh-btn"><RefreshCw className="w-4 h-4" />Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard icon={AlertTriangle} label="Overdue / Missed" value={s.overdue} tone="red" testid="stat-overdue" target="overdue-section" />
        <StatCard icon={CalendarClock} label="Due This Window" value={s.dueSoon} tone="amber" testid="stat-duesoon" target="duesoon-section" />
        <StatCard icon={Repeat} label="Ready to Re-Book" value={s.rebook} tone="green" testid="stat-rebook" target="rebook-section" />
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="space-y-6">
          {/* OVERDUE */}
          <Card className="border-slate-200 overflow-hidden" data-testid="overdue-section">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-red-50/60 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500" />
              <h2 className="font-bold text-slate-900">Overdue &amp; Missed Jobs</h2>
              <Badge className="bg-red-100 text-red-700 border-0 ml-1">{s.overdue}</Badge>
            </div>
            {data.overdue.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">🎉 Nothing overdue. You're all caught up.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {data.overdue.map(wo => (
                  <div key={wo.id} className="px-5 py-3 flex flex-wrap items-center gap-3 hover:bg-slate-50" data-testid={`overdue-row-${wo.id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <button onClick={() => wo.customerId && nav(`/app/customers/${wo.customerId}`)} className="font-semibold text-slate-900 hover:text-[#6FA82C] text-left">{wo.customer || 'Unknown'}</button>
                      <div className="text-xs text-slate-500">{wo.service} · {wo.date}{wo.tech ? ` · ${wo.tech}` : ''}</div>
                    </div>
                    <Badge className="bg-red-100 text-red-700 border-0">{wo.daysOverdue}d overdue</Badge>
                    <Badge variant="outline" className="text-slate-500">{wo.status || 'Open'}</Badge>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="outline" disabled={busy === wo.id} onClick={() => remind(wo)} className="gap-1 h-8 bg-white" data-testid={`remind-${wo.id}`}><Bell className="w-3.5 h-3.5" />Remind</Button>
                      <Button size="sm" disabled={busy === wo.id} onClick={() => markComplete(wo)} className="gap-1 h-8 bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid={`complete-${wo.id}`}><CheckCircle2 className="w-3.5 h-3.5" />Done</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* DUE SOON */}
          <Card className="border-slate-200 overflow-hidden" data-testid="duesoon-section">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-amber-50/60 flex items-center gap-2">
              <CalendarClock className="w-4 h-4 text-amber-500" />
              <h2 className="font-bold text-slate-900">Coming Up</h2>
              <Badge className="bg-amber-100 text-amber-700 border-0 ml-1">{s.dueSoon}</Badge>
            </div>
            {data.dueSoon.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">No jobs scheduled in this window.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[360px] overflow-y-auto">
                {data.dueSoon.map(wo => (
                  <div key={wo.id} className="px-5 py-3 flex flex-wrap items-center gap-3 hover:bg-slate-50" data-testid={`duesoon-row-${wo.id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <button onClick={() => wo.customerId && nav(`/app/customers/${wo.customerId}`)} className="font-semibold text-slate-900 hover:text-[#6FA82C] text-left">{wo.customer || 'Unknown'}</button>
                      <div className="text-xs text-slate-500">{wo.service} · {wo.date} {wo.time}{wo.tech ? ` · ${wo.tech}` : ''}</div>
                    </div>
                    <Badge className="bg-amber-100 text-amber-700 border-0">{wo.inDays === 0 ? 'Today' : `in ${wo.inDays}d`}</Badge>
                    <Button size="sm" variant="outline" disabled={busy === wo.id} onClick={() => remind(wo)} className="gap-1 h-8 bg-white" data-testid={`remind-soon-${wo.id}`}><Bell className="w-3.5 h-3.5" />Send Reminder</Button>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* REBOOK */}
          <Card className="border-slate-200 overflow-hidden" data-testid="rebook-section">
            <div className="px-5 py-3.5 border-b border-slate-100 bg-[#8BC53F]/10 flex items-center gap-2">
              <Repeat className="w-4 h-4 text-[#6FA82C]" />
              <h2 className="font-bold text-slate-900">Auto-Book Next Visit</h2>
              <Badge className="bg-[#8BC53F]/20 text-[#5e8a1f] border-0 ml-1">{s.rebook}</Badge>
              {data.rebook.length > 0 && (
                <Button size="sm" disabled={busy === 'all'} onClick={bookAll} className="ml-auto gap-1.5 bg-[#0d2818] hover:bg-[#16351f]" data-testid="book-all-btn">
                  {busy === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}Book All
                </Button>
              )}
            </div>
            {data.rebook.length === 0 ? (
              <div className="px-5 py-10 text-center text-slate-400 text-sm">No recurring customers are due for re-booking right now.</div>
            ) : (
              <div className="divide-y divide-slate-100 max-h-[420px] overflow-y-auto">
                {data.rebook.map(c => (
                  <div key={c.customerId} className="px-5 py-3 flex flex-wrap items-center gap-3 hover:bg-slate-50" data-testid={`rebook-row-${c.customerId}`}>
                    <div className="flex-1 min-w-[200px]">
                      <button onClick={() => nav(`/app/customers/${c.customerId}`)} className="font-semibold text-slate-900 hover:text-[#6FA82C] text-left">{c.customer}</button>
                      <div className="text-xs text-slate-500">{c.service} · last visit {c.lastVisit}</div>
                    </div>
                    <Badge className="bg-[#8BC53F]/20 text-[#5e8a1f] border-0">{c.frequencyLabel}</Badge>
                    {c.daysOverdue > 0 && <Badge className="bg-red-100 text-red-700 border-0">{c.daysOverdue}d late</Badge>}
                    <div className="text-xs text-slate-500 flex items-center gap-1"><CalendarPlus className="w-3.5 h-3.5" />{c.suggestedDate}</div>
                    <Button size="sm" disabled={busy === c.customerId} onClick={() => book(c)} className="gap-1 h-8 bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid={`book-${c.customerId}`}>
                      {busy === c.customerId ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CalendarPlus className="w-3.5 h-3.5" />}Book
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
